import { CellType } from "@/apis/ai/types";
import type { CellInfo } from "@/apis/table/types";
import type { TaggingRecommendation } from "@/apis/tagging/types";
import { useIsWorkbookAnalyzing } from "@/hooks/use-is-workbook-analyzing";
import queries from "@/queries";
import { useQuery } from "@tanstack/react-query";

/** 보고 있는 표가 이만큼 태깅돼 있으면 강조하지 않는다. */
const TAGGED_ENOUGH_RATIO = 0.3;

// 태깅 대상(DATA/SUM)이 이 개수 이하면 채울 것이 없는 표로 본다. 텍스트만 있는 주석 표는
// 제목 한 줄이 DATA 로 잡히곤 해서, "0 개"로만 끊으면 그런 표가 계속 안내에 걸린다.
const NEGLIGIBLE_TARGET_COUNT = 1;

/**
 * 이 표 위에서는 안내를 접는지 — 태깅 대상(DATA/SUM)이 없다시피 하거나, 그중 태그가
 * 하나라도 붙은 비율이 임계 이상이면 접는다.
 */
const shouldSkipGuidance = (cells: CellInfo[]): boolean => {
  const targets = cells.filter(
    (cell) => cell.type === CellType.DATA || cell.type === CellType.SUM,
  );
  if (targets.length <= NEGLIGIBLE_TARGET_COUNT) return true;

  const tagged = targets.filter((cell) => cell.taggedInfos.tags.length > 0);
  return tagged.length / targets.length >= TAGGED_ENOUGH_RATIO;
};

/**
 * 워크북 진입 시 강조할 다음 태깅 액션을 서버에서 가져온다.
 *
 * 빈 표·소스 유무·표별 롤포워드 이력으로 백엔드가 판정한다. NONE 이면 강조하지 않는다.
 * 분석 중에는 아예 부르지 않는다 — 그때 받은 답(NONE)이 캐시에 굳으면 분석이 끝나도
 * 안내가 뜨지 않는다. 완료 시점의 재조회는 `useXbrlAnalysisCompletionEffect` 가 맡는다.
 *
 * 백엔드 판정은 워크북 단위지만, 지금 보고 있는 표에 태깅할 것이 없거나 이미 충분히
 * 태깅돼 있으면 접는다. 그 표 위에서 다음 액션을 강조해봐야 잔소리다.
 */
export function useTaggingGuidance(
  workbookId: string | undefined,
  tableId: string | null,
): TaggingRecommendation {
  const isAnalyzing = useIsWorkbookAnalyzing(workbookId);

  const { data } = useQuery({
    ...queries.workbook(workbookId ?? "")._ctx.taggingGuidance(),
    enabled: !!workbookId && !isAnalyzing,
    // 워크북당 1회만 받아 표 이동 간 재사용한다. 상태가 바뀌는 지점(오토태깅/롤포워드
    // 완료 → 워크북 루트 invalidate, 태그 리셋, 분석 완료)에서 명시적으로 무효화해 갱신한다.
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });

  const { data: skipGuidance } = useQuery({
    ...queries
      .workbook(workbookId ?? "")
      ._ctx.tables(tableId ?? "")
      ._ctx.cells(),
    enabled: !!workbookId && !!tableId,
    select: (cellsList) => shouldSkipGuidance(cellsList.cells),
  });

  const recommendation = data?.recommendation ?? "NONE";
  if (!tableId) return recommendation;
  // undefined 는 셀을 아직 못 받은 상태다. 잠깐 강조했다 끄면 툴팁이 그 하루치를 그대로
  // 소진하므로(하루 1회), 판단이 설 때까지는 접어둔다.
  if (skipGuidance === undefined || skipGuidance) return "NONE";
  return recommendation;
}
